'use client';
import cn from 'classnames';
import Image from 'next/image';
import Link from 'next/link';
import RichText from '@/components/common/RichText/RichText';
import Button from '@/components/common/Button/Button';
import Notch from '@/components/common/Notch/Notch';

import './Hero.style.scss';

type HeroProps = {
  /** ClassName for styling overrides */
  className?: string;

  /** Main text to display to the end user. Rich Text HTML field */
  displayText: string;

  /** `true` will restrict text block to be no larger than 50%. Otherwise 70% */
  halfVariant?: boolean;

  /** Gives granular control to Builder for top position offset */
  desktopTopPosition: number;

  /** Gives granular control to Builder for left position offset */
  desktopLeftPosition: number;

  /** Text to show for the button */
  buttonText?: string;

  /** URL to send the user to when the click the button */
  buttonLink?: string;

  /** [DESKTOP] Builder.io image string */
  image: string;

  /** Width of image to pass to Next's <Image> component */
  imgWidth: number;

  /** Height of image to pass to Next's <Image> component */
  imgHeight: number;

  /** Alt text to show for the image for accessibility */
  imgAltText: string;

  /** [MOBILE] Builder.io image string */
  imageMobile: string;

  /** [MOBILE] Width of image to pass to Next's <Image> component */
  imageMobileWidth: number;

  /** [MOBILE] Height of image to pass to Next's <Image> component */
  imageMobileHeight: number;

  /** [MOBILE] Alt text to show for the image for accessibility */
  imageMobileAltText: string;

  /** Show the notch SVG if true */
  showNotch: boolean;
};

function Hero(props: HeroProps) {
  const {
    className = '',
    displayText,
    halfVariant = false,
    desktopLeftPosition = 5,
    desktopTopPosition = 40,
    buttonText,
    buttonLink,
    image,
    imgAltText,
    imgHeight,
    imgWidth,
    imageMobile,
    imageMobileWidth,
    imageMobileHeight,
    imageMobileAltText,
    showNotch
  } = props;

  return (
    <div className={cn('Hero', className)}>
      <div className='Hero-img-container'>
        <Image
          className='Hero-img'
          alt={imgAltText}
          src={image}
          height={imgHeight}
          width={imgWidth}
        />
        <Image
          className='Hero-img-mobile'
          alt={imageMobileAltText}
          src={imageMobile}
          height={imageMobileHeight}
          width={imageMobileWidth}
        />
        <div
          className={cn('Hero-float-section', { 'half-variant': halfVariant })}
          style={{
            top: `${desktopTopPosition}%`,
            left: `${desktopLeftPosition}%`
          }}
        >
          <RichText className='Hero-text'>{displayText}</RichText>
          {buttonText && buttonLink && (
            <div className='Hero-link-wrapper'>
              <Link
                className='Hero-link'
                href={buttonLink}
              >
                <Button>{buttonText}</Button>
              </Link>
            </div>
          )}
        </div>
        {showNotch && <Notch className='Hero-notch' />}
      </div>
    </div>
  );
}

export default Hero;
